import React, { useState, useEffect } from "react";
import axios from "axios";
import Menu from "../Menu";
import { useNavigate } from "react-router-dom";
import SuccessMessage from "../../components/successMessage";
import FailedMessage from "../../components/FailedMessage";
import Switch from "../../components/Switch";
import useApiUrls from "../../useApiUrls";
import Pagination from "../components/Pagination";

const SubGroup = () => {
  const [data, setData] = useState([]);
  const [dataLimit, setDataLimit] = useState(100);
  const [count, setCount] = useState(0);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalItems, setTotalItems] = useState(0);

  const navigate = useNavigate();
  const { getUrlByOperation } = useApiUrls();

  const token = getCookie("token");
  const userid = getCookie("userid");

  // Allocation State
  const [showAllocation, setShowAllocation] = useState(false);
  const [allocationType, setAllocationType] = useState("POS");
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [leftList, setLeftList] = useState([]);
  const [rightList, setRightList] = useState([]);
  const [selectedLeft, setSelectedLeft] = useState([]);
  const [selectedRight, setSelectedRight] = useState([]);

  // SubGroup dropdown state
  const [subGroupOptions, setSubGroupOptions] = useState([]);
  const [selectedSubGroupId, setSelectedSubGroupId] = useState("");
  const [originalRightList, setOriginalRightList] = useState([]);
  const [allAvailableItems, setAllAvailableItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  /* ================= FETCH SUBGROUPS FOR DROPDOWN ================= */
  const fetchSubGroups = async (groupId, availableItems = null) => {
    // Use passed availableItems or fall back to state
    const itemsToUse = availableItems || allAvailableItems;

    try {
      const res = await axios.post(
        "https://api.dqr-display-tms.eventum.co.in/api/SubGroupOperation/Search",
        {
          id: 0,
          groupid: groupId,
          ispageload: true,
          pagesize: 1000,
          currentpageno: 0,
          withoutpagination: true,
          pagedirection: "string",
        },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
            accept: "text/plain",
          },
        }
      );
      const subgroups = res.data?.data || [];
      setSubGroupOptions(subgroups);

      // Auto-select first subgroup if available
      if (subgroups.length > 0) {
        setSelectedSubGroupId(subgroups[0].id);
        fetchAllocatedItems(subgroups[0].id, itemsToUse);
      } else {
        setSelectedSubGroupId("");
        setRightList([]);
        setOriginalRightList([]);
        setLeftList(itemsToUse);
      }
    } catch (error) {
      console.error("Error fetching subgroups:", error);
      setMessage("Failed to fetch subgroups");
    }
  };

  /* ================= FETCH ALLOCATED ITEMS FOR SELECTED SUBGROUP ================= */
  const fetchAllocatedItems = async (subgroupId, availableItems = null) => {
    // Use passed availableItems or fall back to state
    const itemsToFilter = availableItems || allAvailableItems;

    if (!subgroupId) {
      setRightList([]);
      setOriginalRightList([]);
      setLeftList(itemsToFilter);
      return;
    }

    try {
      const res = await axios.post(
        "https://api.dqr-display-tms.eventum.co.in/api/SubGroupDataOperation/Search",
        {
          subgroupid: parseInt(subgroupId),
          ispageload: true,
          pagesize: 1000,
          currentpageno: 0,
          withoutpagination: true,
          pagedirection: "string",
        },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
            accept: "text/plain",
          },
        }
      );
      const allocatedData = res.data?.data || [];
      setRightList(allocatedData);
      setOriginalRightList(allocatedData);

      // Filter left list: show only items that are NOT in allocated data
      // Use posid from allocated data (not id, which is the allocation record ID)
      const allocatedPosIds = allocatedData.map(item => item.posid);
      const filteredLeft = itemsToFilter.filter(
        item => !allocatedPosIds.includes(item.id)
      );
      setLeftList(filteredLeft);

    } catch (error) {
      console.error("Error fetching allocated items:", error);
      setMessage("Failed to fetch allocated items");
    }
  };

  /* ================= FETCH ALL AVAILABLE ITEMS ================= */
  const fetchAllAvailableItems = async () => {
    try {
      const res = await axios.get(
        "https://api.dqr-display-tms.eventum.co.in/api/SubGroupDataOperation/Get/NonAssign",
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            accept: "text/plain",
          },
        }
      );
      const allItems = res.data?.data || [];
      setAllAvailableItems(allItems);
      setLeftList(allItems);
      return allItems; // Return items for immediate use
    } catch (error) {
      console.error("Error fetching available items:", error);
      setMessage("Failed to fetch available items");
      return [];
    }
  };

  /* ================= HANDLE SUBGROUP SELECTION ================= */
  const handleSubGroupChange = (subgroupId) => {
    setSelectedSubGroupId(subgroupId);
    setSelectedLeft([]);
    setSelectedRight([]);
    fetchAllocatedItems(subgroupId);
  };

  /* ================= GET DISPLAY TEXT ================= */
  const getDisplayText = (item) => {
    const posName = item.posname || item.name || item.value || "N/A";
    const storeId = item.storeid || "N/A";
    const merchantName = item.merchantid || "N/A";
    return `${posName}, ${storeId}, ${merchantName}`;
  };

  /* ================= MOVE LOGIC ================= */
  const moveRight = () => {
    if (selectedLeft.length === 0) return;

    const itemsToMove = leftList.filter(item => selectedLeft.includes(item.id));
    const newRightList = [...rightList, ...itemsToMove];
    const newLeftList = leftList.filter(item => !selectedLeft.includes(item.id));

    setRightList(newRightList);
    setLeftList(newLeftList);
    setSelectedLeft([]);
  };

  const moveLeft = () => {
    if (selectedRight.length === 0) return;

    const itemsToMove = rightList.filter(item => selectedRight.includes(item.id));
    const newLeftList = [...leftList, ...itemsToMove];
    const newRightList = rightList.filter(item => !selectedRight.includes(item.id));

    setLeftList(newLeftList);
    setRightList(newRightList);
    setSelectedRight([]);
  };

  /* ================= DRAG & DROP ================= */
  const onDragStart = (e, item) => {
    e.dataTransfer.setData("item", JSON.stringify(item));
  };

  const onDropRight = (e) => {
    e.preventDefault();
    try {
      const item = JSON.parse(e.dataTransfer.getData("item"));

      if (!rightList.find(i => i.id === item.id)) {
        setRightList([...rightList, item]);
        setLeftList(leftList.filter(i => i.id !== item.id));
      }
    } catch (error) {
      console.error("Error in drag drop:", error);
    }
  };

  const onDropLeft = (e) => {
    e.preventDefault();
    try {
      const item = JSON.parse(e.dataTransfer.getData("item"));

      if (!leftList.find(i => i.id === item.id)) {
        setLeftList([...leftList, item]);
        setRightList(rightList.filter(i => i.id !== item.id));
      }
    } catch (error) {
      console.error("Error in drag drop:", error);
    }
  };

  /* ================= OPEN ALLOCATION MODAL ================= */
  const handleOpenAllocation = async (groupId) => {
    setSelectedGroupId(groupId);
    setShowAllocation(true);
    setAllocationType("POS");
    setSelectedLeft([]);
    setSelectedRight([]);
    setSelectedSubGroupId("");
    setRightList([]);
    setOriginalRightList([]);
    setLeftList([]);
    setSubGroupOptions([]);

    // First fetch all available items, then fetch subgroups with the items
    const availableItems = await fetchAllAvailableItems();
    fetchSubGroups(groupId, availableItems);
  };

  /* ================= CLOSE ALLOCATION MODAL ================= */
  const handleCloseAllocation = () => {
    setShowAllocation(false);
    setSelectedGroupId(null);
    setLeftList([]);
    setRightList([]);
    setOriginalRightList([]);
    setSelectedLeft([]);
    setSelectedRight([]);
    setSubGroupOptions([]);
    setSelectedSubGroupId("");
    setAllAvailableItems([]);
  };

  /* ================= SAVE ALLOCATION ================= */
  const handleSaveAllocation = async () => {
    if (!selectedSubGroupId) {
      setMessage("Please select a subgroup first");
      return;
    }

    setLoading(true);
    try {
      // Find items to INSERT (items that are in rightList but were not in originalRightList)
      const itemsToInsert = rightList.filter(
        rightItem => !originalRightList.some(origItem => origItem.id === rightItem.id)
      );

      // Find items to DELETE (items that were in originalRightList but are now in leftList)
      const itemsToDelete = originalRightList.filter(
        origItem => !rightList.some(rightItem => rightItem.id === origItem.id)
      );

      // INSERT new allocations
      const insertPromises = itemsToInsert.map(item =>
        axios.post(
          "https://api.dqr-display-tms.eventum.co.in/api/SubGroupDataOperation/Insert",
          {
            subgroupid: parseInt(selectedSubGroupId),
            posname: item.posname || item.name || item.value,
            merchantid: item.merchantid,
            storeid: item.storeid || "",
            remark: item.remark || "",
            active: true
          },
          {
            headers: {
              "X-Authorization": token,
              UserId: userid,
              "Content-Type": "application/json-patch+json",
              accept: "text/plain",
            },
          }
        )
      );

      // DELETE removed allocations
      const deletePromises = itemsToDelete.map(item =>
        axios.post(
          "https://api.dqr-display-tms.eventum.co.in/api/SubGroupDataOperation/Delete",
          {
            id: item.id
          },
          {
            headers: {
              "X-Authorization": token,
              UserId: userid,
              "Content-Type": "application/json-patch+json",
              accept: "text/plain",
            },
          }
        )
      );

      // Execute all operations
      const results = await Promise.all([...insertPromises, ...deletePromises]);
      const failure = results.find(
        (r) => r.data && (r.data.isresponse === false || r.data.responsestatus === "FAIL")
      );
      if (failure) {
        setMessage(failure.data?.errormsg || failure.data?.message || "Failed to save allocation");
      } else {
        setMessage("Allocation saved successfully!");
        setTimeout(() => {
          handleCloseAllocation();
        }, 1500);
      }

    } catch (error) {
      console.error("Error saving allocation:", error);
      const errorMsg =
        error.response?.data?.errormsg ||
        error.response?.data?.message ||
        error.message ||
        "Failed to save allocation";
      setMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  /* ================= FETCH GROUP DATA ================= */
  const fetchGroupData = async (showLoader = false) => {
    if (showLoader) {
      setLoading(true);
    }
    try {
      const GroupSearch = getUrlByOperation("GroupSearch") ||
        "https://api.dqr-display-tms.eventum.co.in/api/GroupOperation/Search";

      const res = await axios.post(
        GroupSearch,
        {
          id: 0,
          applycreateddate: false,
          fromcreateddate: 0,
          endcreateddate: 0,
          active: "",
          groupname: "",
          remark: "",
          ispageload: false,
          pagesize: dataLimit,
          currentpageno: count,
          withoutpagination: false,
          pagedirection: "",
        },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
            accept: "text/plain",
          },
        }
      );

      if (res.data && Array.isArray(res.data.data)) {
        const rows = res.data.data;
        setData(rows);
        setHasMore(rows.length === dataLimit);
        const approximateTotal = (count * dataLimit) + rows.length + (rows.length === dataLimit ? dataLimit : 0);
        setTotalItems(approximateTotal);
      } else {
        setData([]);
        setHasMore(false);
        setTotalItems(0);
      }
    } catch (error) {
      console.error("Error fetching group data:", error);
      setData([]);
      setHasMore(false);
      setTotalItems(0);
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchGroupData(true);
    const interval = setInterval(() => {
      fetchGroupData(false);
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLimit, count]);

  const handlePageChange = (page) => {
    setCount(page - 1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setDataLimit(newItemsPerPage);
    setCount(0);
  };

  const totalPages = hasMore ? count + 2 : count + 1;

  const handleAddGroup = () => {
    navigate("/addgroup");
  };

  const handleUpdateGroup = (id) => {
    navigate(`/updategroup/${id}`);
  };

  const handleViewSubGroup = (id) => {
    navigate(`/subgroup/${id}`);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this group?")) {
      try {
        const GroupDelete = getUrlByOperation("GroupDelete") ||
          "https://api.dqr-display-tms.eventum.co.in/api/GroupOperation/Delete";

        const res = await axios.post(
          GroupDelete,
          { id },
          {
            headers: {
              "X-Authorization": token,
              UserId: userid,
              "Content-Type": "application/json-patch+json",
              accept: "text/plain",
            },
          }
        );
        if (
          res.data &&
          (res.data.isresponse === true ||
            res.data.responsestatus === "SUCCESS")
        ) {
          setMessage("Group deleted successfully!");
          fetchGroupData();
        } else {
          setMessage(
            res.data?.errormsg || res.data?.message || "Failed to delete group"
          );
        }
      } catch (error) {
        console.error("Error deleting group:", error);
        const errorMsg =
          error.response?.data?.errormsg ||
          error.response?.data?.message ||
          error.message ||
          "Failed to delete group";
        setMessage(errorMsg);
      }
    }
  };

  const handleToggle = async (item) => {
    try {
      const apiEndpoint = item.active
        ? "https://api.dqr-display-tms.eventum.co.in/api/GroupOperation/UpdateStatus/Deactive"
        : "https://api.dqr-display-tms.eventum.co.in/api/GroupOperation/UpdateStatus/Active";

      const GroupActive = getUrlByOperation("GroupActive");
      const GroupDeactive = getUrlByOperation("GroupDeactive");

      const url = item.active
        ? (GroupDeactive || apiEndpoint.replace("Active", "Deactive"))
        : (GroupActive || apiEndpoint);

      await axios.post(
        url,
        { id: item.id },
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
            accept: "text/plain",
          },
        }
      );
      setData(data.map((d) => (d.id === item.id ? { ...d, active: !item.active } : d)));
      setMessage(item.active ? "Group deactivated successfully!" : "Group activated successfully!");
    } catch (error) {
      console.error("Error updating group status:", error);
      setMessage("Failed to update group status");
      if (error.message === "relogin token expire") {
        navigate("/login");
      }
    }
  };

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="d-flex justify-content-between align-items-center w-100">
                <h1 className="mb-0">Groups</h1>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleAddGroup}
                >
                  Add Group
                </button>
              </div>
            </div>
          </section>

          <section className="content">
            <div className="container-fluid">
              <hr />
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-body table-responsive p-0">
                      <table className="table table-hover table-striped">
                         <thead className="" style={{
                            backgroundColor: 'var(--surface2)',
                            color: 'var(--text)',
                            borderColor: 'var(--border)'
                          }}>
                          <tr>
                            <th style={{ width: "5%" }} className="text-center">
                              ID
                            </th>
                            <th style={{ width: "20%" }}>Group Name</th>
                            <th style={{ width: "30%" }}>Description</th>
                            <th style={{ width: "20%" }} className="text-center">
                              Actions
                            </th>
                            <th style={{ width: "15%" }} className="text-center">
                              Active
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr>
                              <td colSpan="5" className="text-center">
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                {" "}Loading...
                              </td>
                            </tr>
                          ) : data.length > 0 ? (
                            data.map((item, index) => (
                              <tr key={index}>
                                <td className="text-center">{item.id}</td>
                                <td>
                                  <strong>{item.groupname || item.name || "-"}</strong>
                                </td>
                                <td>{item.description || item.remark || "-"}</td>
                                <td className="text-center">
                                  <div className="btn-group">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-info mr-1"
                                      onClick={() => handleViewSubGroup(item.id)}
                                      title="View SubGroup"
                                    >
                                      <i className="fas fa-list"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary mr-1"
                                      onClick={() => handleUpdateGroup(item.id)}
                                      title="Edit Group"
                                    >
                                      <i className="fas fa-edit"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger mr-1"
                                      onClick={() => handleDelete(item.id)}
                                      title="Delete Group"
                                    >
                                      <i className="fas fa-trash"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-warning"
                                      style={{ color: "var(--text)" }}
                                      onClick={() => handleOpenAllocation(item.id)}
                                      title="Allocation"
                                    >
                                      Allocation
                                    </button>
                                  </div>
                                </td>
                                <td className="text-center">
                                  <Switch
                                    isOn={item.active}
                                    handleToggle={() => handleToggle(item)}
                                    id={item.id}
                                  />
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" className="text-center">
                                No groups found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <Pagination
                      currentPage={count + 1}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                      itemsPerPage={dataLimit}
                      onItemsPerPageChange={handleItemsPerPageChange}
                      totalItems={totalItems}
                      showPageNumbers={false}
                      showItemsPerPageSelector={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* ALLOCATION MODAL */}
      {showAllocation && (
        <>
          <div
            onClick={handleCloseAllocation}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              zIndex: 1040,
            }}
          />

          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "88%",
              maxWidth: "1100px",
              maxHeight: "88vh",
              background: "#fff",
              zIndex: 1050,
              borderRadius: "4px",
              boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
              overflowY: "auto",
              border: "1px solid #d9d9d9"
            }}
          >
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                padding: "10px 16px",
                borderBottom: "1px solid #d9d9d9",
                backgroundColor: "#f8f9fa"
              }}
            >
              <h5 className="mb-0" style={{ color: "#1f2937" }}>
                Group Allocation
              </h5>
              <button
                className="btn btn-sm btn-danger"
                onClick={handleCloseAllocation}
                style={{ fontSize: "16px", padding: "2px 10px" }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: "14px 16px" }}>
              <div
                className="row"
                style={{
                  marginBottom: "14px",
                  border: "1px solid #d9d9d9",
                  borderRadius: "3px",
                  padding: "12px 8px",
                  backgroundColor: "#fcfcfc"
                }}
              >
                <div className="col-md-6">
                  <label className="font-weight-bold mb-1">Group</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={selectedGroupId || ""}
                    readOnly
                  />
                </div>
                <div className="col-md-6">
                  <label className="font-weight-bold mb-1">
                    Select SubGroup <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control form-control-sm"
                    value={selectedSubGroupId}
                    onChange={(e) => handleSubGroupChange(e.target.value)}
                  >
                    <option value="">-- Select SubGroup --</option>
                    {subGroupOptions.map((subgroup) => (
                      <option key={subgroup.id} value={subgroup.id}>
                        {subgroup.subgroupname || subgroup.name || subgroup.groupname || `SubGroup ${subgroup.id}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-12">
                  <label className="font-weight-bold mb-1">Search</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search POS, Store or Merchant..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

            <div className="row">
              <div className="col-md-5">
                <label className="font-weight-bold mb-2 d-block">
                  Available Items ({leftList.length})
                </label>
                <div
                  onDrop={onDropLeft}
                  onDragOver={(e) => e.preventDefault()}
                  style={{
                    border: "1px solid #d9d9d9",
                    borderRadius: "3px",
                    height: "330px",
                    overflowY: "auto",
                    padding: "8px",
                    backgroundColor: "#ffffff"
                  }}
                >
                  {leftList.length === 0 ? (
                    <div className="text-center text-muted py-5">
                      No items available
                    </div>
                  ) : (
                    leftList
                      .filter((item) => {
                        if (!searchTerm) return true;
                        const text = getDisplayText(item).toLowerCase();
                        return text.includes(searchTerm.toLowerCase());
                      })
                      .map((item) => (
                        <div
                          key={`left-${item.id}`}
                          draggable
                          onDragStart={(e) => onDragStart(e, item)}
                          className="p-2 mb-2 border rounded d-flex align-items-center"
                          style={{ cursor: "move" }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedLeft.includes(item.id)}
                            onChange={(e) =>
                              setSelectedLeft((prev) =>
                                e.target.checked
                                  ? [...prev, item.id]
                                  : prev.filter((id) => id !== item.id)
                              )
                            }
                            className="mr-2"
                          />
                          <div>
                            <strong>{getDisplayText(item)}</strong>
                            <br />
                            <small className="text-muted">
                              ID: {item.id}
                            </small>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>

              <div className="col-md-2 d-flex flex-column justify-content-center align-items-center">
                <button
                  className="btn btn-primary mb-2"
                  onClick={moveRight}
                  disabled={selectedLeft.length === 0 || !selectedSubGroupId}
                  style={{ width: "42px", height: "32px", padding: 0 }}
                  title="Move selected items to allocated"
                >
                  →
                </button>
                <button
                  className="btn btn-primary"
                  onClick={moveLeft}
                  disabled={selectedRight.length === 0}
                  style={{ width: "42px", height: "32px", padding: 0 }}
                  title="Move selected items back to available"
                >
                  ←
                </button>
              </div>

              <div className="col-md-5">
                <label className="font-weight-bold mb-2 d-block">
                  Allocated Items ({rightList.length})
                  {selectedSubGroupId && (
                    <span className="badge badge-info ml-2" style={{ fontSize: "11px" }}>
                      SubGroup ID: {selectedSubGroupId}
                    </span>
                  )}
                </label>
                <div
                  onDrop={onDropRight}
                  onDragOver={(e) => e.preventDefault()}
                  style={{
                    border: "1px solid #d9d9d9",
                    borderRadius: "3px",
                    height: "330px",
                    overflowY: "auto",
                    padding: "8px",
                    backgroundColor: "#ffffff"
                  }}
                >
                  {!selectedSubGroupId ? (
                    <div className="text-center text-warning py-5">
                      <i className="fas fa-info-circle fa-2x mb-2"></i>
                      <p>Please select a SubGroup first</p>
                    </div>
                  ) : rightList.length === 0 ? (
                    <div className="text-center text-muted py-5">
                      No items allocated to this subgroup
                    </div>
                  ) : (
                    rightList.map((item) => (
                      <div
                        key={`right-${item.id}`}
                        draggable
                        onDragStart={(e) => onDragStart(e, item)}
                        className="p-2 mb-2 border rounded d-flex align-items-center"
                        style={{ cursor: "move" }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedRight.includes(item.id)}
                          onChange={(e) =>
                            setSelectedRight((prev) =>
                              e.target.checked
                                ? [...prev, item.id]
                                : prev.filter((id) => id !== item.id)
                            )
                          }
                          className="mr-2"
                        />
                        <div>
                          <strong>{getDisplayText(item)}</strong>
                          <br />
                          <small className="text-muted">
                            ID: {item.id}
                          </small>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="row mt-3">
              <div className="col-12 text-muted" style={{ fontSize: "13px" }}>
                Available: {leftList.length} | Allocated: {rightList.length} | Selected:{" "}
                {selectedLeft.length + selectedRight.length}
              </div>
            </div>

            <div
              className="d-flex justify-content-end"
              style={{ marginTop: "16px", borderTop: "1px solid #d9d9d9", paddingTop: "12px" }}
            >
              <button
                className="btn btn-secondary mr-2"
                onClick={handleCloseAllocation}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="btn btn-success"
                onClick={handleSaveAllocation}
                disabled={!selectedSubGroupId || loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  "Save Allocation"
                )}
              </button>
            </div>
            </div>
          </div>
        </>
      )}

      {message && (
        message.includes("successfully") ? (
          <SuccessMessage message={message} onClose={() => setMessage("")} />
        ) : (
          <FailedMessage message={message} onClose={() => setMessage("")} />
        )
      )}
    </>
  );
};

export default SubGroup;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}